import React, { useState, useEffect, useCallback } from 'react';
import { representanteAPI } from '../../services/api';

const estadoConfig = {
  pendiente: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
  confirmado: { label: 'Confirmado', color: 'bg-green-100 text-green-800', icon: '✅' },
  rechazado: { label: 'Rechazado', color: 'bg-red-100 text-red-800', icon: '❌' },
};

const conceptoLabels = {
  mensualidad: 'Mensualidad',
  uniforme: 'Uniforme',
  torneo: 'Torneo',
  otro: 'Otro',
};

const meses = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const HistorialPagos = ({ representanteId }) => {
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');

  const cargarPagos = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filtroEstado) params.estado = filtroEstado;
      const response = await representanteAPI.getMisPagos(params);
      setPagos(response.data);
    } catch (error) {
      console.error('Error loading payments:', error);
    } finally {
      setLoading(false);
    }
  }, [filtroEstado]);

  useEffect(() => {
    if (representanteId) {
      cargarPagos();
    }
  }, [representanteId, cargarPagos]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatMonto = (monto) => {
    if (!monto) return '-';
    return parseFloat(monto).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-gray-800">Historial de Pagos</h2>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { value: '', label: 'Todos' },
          { value: 'pendiente', label: '⏳ Pendientes' },
          { value: 'confirmado', label: '✅ Confirmados' },
          { value: 'rechazado', label: '❌ Rechazados' },
        ].map((filtro) => (
          <button
            key={filtro.value}
            onClick={() => setFiltroEstado(filtro.value)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              filtroEstado === filtro.value
                ? 'bg-lsfc-green-600 text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {filtro.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-lsfc-green-600"></div>
        </div>
      ) : pagos.length === 0 ? (
        <div className="text-center py-12">
          <span className="text-5xl">📋</span>
          <h3 className="text-lg font-bold text-gray-600 mt-3">
            {filtroEstado ? 'No hay pagos con este estado' : 'No hay pagos registrados'}
          </h3>
          <p className="text-gray-400 mt-1 text-sm">
            {filtroEstado ? 'Prueba con otro filtro' : 'Registra tu primer pago en la pestaña "Registrar Pago"'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {pagos.map((pago) => {
            const estado = estadoConfig[pago.estado] || estadoConfig.pendiente;
            return (
              <div key={pago.id} className="bg-white rounded-xl shadow-md p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-gray-800 text-sm">
                        {conceptoLabels[pago.concepto] || pago.concepto}
                      </h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${estado.color}`}>
                        {estado.icon} {estado.label}
                      </span>
                    </div>

                    {/* Player name */}
                    <p className="text-xs text-gray-500 mt-1">
                      👤 {pago.jugador_nombres} {pago.jugador_apellidos}
                    </p>

                    {/* Month/year for mensualidad */}
                    {pago.concepto === 'mensualidad' && pago.mes_correspondiente && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        📅 {meses[pago.mes_correspondiente - 1]} {pago.ano_correspondiente}
                      </p>
                    )}

                    {/* Payment method */}
                    <p className="text-xs text-gray-400 mt-0.5">
                      💳 {pago.metodo_pago?.replace(/_/g, ' ') || 'Sin método'}
                      {pago.referencia && ` • Ref: ${pago.referencia}`}
                    </p>
                  </div>

                  {/* Amount */}
                  <div className="text-right flex-shrink-0 ml-3">
                    <p className="font-bold text-gray-800">${formatMonto(pago.monto_usd)}</p>
                    <p className="text-xs text-gray-400">Bs. {formatMonto(pago.monto_bs)}</p>
                    <p className="text-xs text-gray-300 mt-1">{formatDate(pago.fecha_pago)}</p>
                  </div>
                </div>

                {/* Rejection reason */}
                {pago.estado === 'rechazado' && pago.notas && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-100 rounded-lg">
                    <p className="text-xs text-red-600">
                      <strong>Motivo:</strong> {pago.notas}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HistorialPagos;
